/**
 * Service-worker registration that can win a hard-reload race.
 *
 * What shipped: vite-plugin-pwa injected a script that only called
 * `navigator.serviceWorker.register('/sw.js')` on window `load`. The generated
 * worker precached `index.html` and used it as the navigation fallback
 * (cache-first). Restore failures in the old frontend called `location.reload()`
 * in ~850ms — often before `load` — so a new `/sw.js` was never fetched while
 * the controlling worker kept serving the stale app shell. Repeated reloads
 * therefore booted the same hashed bundle indefinitely even after Pages had
 * deployed a new one.
 *
 * This module registers immediately, never uses an HTTP-cached copy of `/sw.js`,
 * asks the registration to update, and reloads at most once when a *new* worker
 * takes control of a page that was already controlled. A first-time claim does
 * not reload, but `hadController` is then set so a later distinct claim in the
 * same long-lived tab does reload once. Generated workers also serve document
 * navigations NetworkFirst so a later Pages deploy is not pinned to the
 * controlling worker's precached HTML.
 *
 * Migrating from the pre-#38 worker may take two navigations: the first lets
 * the new worker install and claim; the second boots the new bundle. No manual
 * cache or service-worker reset is required.
 *
 * Browser checks that unit tests cannot replace: `npm run build && npm run preview`,
 * load once so a worker controls the page, replace `dist` with a second build,
 * confirm one `controllerchange` reload and that `window.__ANVIL_BUILD__` matches
 * the new commit; a fresh first visit must not extra-reload; offline after a
 * successful online load must still open the app shell.
 */
export const SW_URL = '/sw.js';

export function registerPwa({
  serviceWorker = globalThis.navigator?.serviceWorker,
  locationRef = globalThis.location,
  documentRef = globalThis.document,
  swUrl = SW_URL,
} = {}) {
  if (!serviceWorker || typeof serviceWorker.register !== 'function') {
    return { enabled: false, reloads: 0, checkForUpdate() {}, ready: Promise.resolve() };
  }

  let hadController = Boolean(serviceWorker.controller);
  let reloading = false;
  let registration = null;
  const state = { enabled: true, reloads: 0, registerOptions: null };

  function reloadOnce() {
    if (reloading) return;
    reloading = true;
    state.reloads += 1;
    locationRef?.reload?.();
  }

  function onControllerChange() {
    // First visit: skipWaiting + clientsClaim will claim this client. Reloading
    // here would make every fresh install bounce, which is not an update. After
    // that claim, later controllerchange events in this same tab are updates.
    const wasControlled = hadController;
    hadController = true;
    if (!wasControlled) return;
    reloadOnce();
  }

  serviceWorker.addEventListener?.('controllerchange', onControllerChange);

  state.registerOptions = { scope: '/', updateViaCache: 'none' };
  const pending = Promise.resolve(serviceWorker.register(swUrl, state.registerOptions))
    .then((next) => {
      registration = next;
      return next?.update?.();
    })
    .catch((error) => {
      console.warn('[anvil] service worker registration failed', error);
    });

  function checkForUpdate() {
    return pending.then(() => registration?.update?.());
  }

  documentRef?.addEventListener?.('visibilitychange', () => {
    if (documentRef.visibilityState === 'visible') void checkForUpdate();
  });

  return {
    enabled: true,
    get reloads() {
      return state.reloads;
    },
    get registerOptions() {
      return state.registerOptions;
    },
    checkForUpdate,
    ready: pending,
  };
}
